import axios, { isCancel } from 'axios'
import _ from 'lodash'
import { toast } from 'vue3-toastify'

import { isExpectedUnauthenticated401, isMedicaidApplyRequest } from '@/plugins/isExpectedUnauthenticated401'
import { rollbar } from '@/plugins/rollbar'
import { summarizeRequestBody } from '@/plugins/rollbarRequestSummary'
import router from '@/router'
import { isPublicRoute } from '@/router/utils'
import { useAuthStore } from '@/stores/auth'

declare module 'axios' {
  export interface AxiosRequestConfig {

    /**
     * Opt-in flag for short-interval polling loops (see `usePollingWithBackoff`).
     * When set, a 429 response for this request is NOT swallowed by the generic
     * "too many requests" toast/never-settling promise and is NOT auto-reported
     * to Rollbar — the poller catches it, backs off, and reports at most once
     * per rate-limited burst itself. Requests without this flag keep the
     * existing global 429 handling unchanged.
     */
    isPollRequest?: boolean

    /**
     * Opt-in flag for requests that must survive a route change fired by the same
     * logical action that started them (e.g. a fire-and-forget follow-up call kicked off
     * right after `router.replace()` navigates to the just-created resource). The
     * `router.beforeEach` guard below cancels every tracked in-flight request on any path
     * change, and the response interceptor deliberately never settles a cancelled
     * request's promise (see the `isCancel` branch) — so a caller `await`-ing one of
     * these hangs forever instead of erroring, with no way to recover. Set this flag
     * to keep the request out of that cancellation sweep entirely, the same way the
     * URL-prefix exemptions below do for global/polling fetches.
     */
    skipRouteChangeCancel?: boolean

    /**
     * Opt-in flag for endpoints whose response body legitimately has a
     * top-level `data` key that is NOT the Laravel-resource-collection
     * envelope the response interceptor below normally unwraps (e.g.
     * `{ patient, data: [...] }` from GET /insurance/patients/{uuid}/insurance-summary).
     * Without this flag, `response.data && response.data.data && !response.data.meta`
     * unconditionally collapses `response.data` to `response.data.data`,
     * silently discarding every sibling key (like `patient`) alongside it.
     */
    skipDataUnwrap?: boolean
  }
}

const axiosIns = axios.create({
  baseURL: `${import.meta.env.VITE_SERVER_URL}/api`,
  timeout: 30000,
  withCredentials: true,
  withXSRFToken: true,
})

const pendingRequests: ReturnType<typeof axios.CancelToken.source>[] = []

/** User-global fetches that must survive boot redirects (e.g. / → interstitial → vector-home). */
const ROUTE_CHANGE_CANCEL_EXEMPT_URL_PREFIXES = [
  '/profile/notifications',
  '/broadcasting/auth',
  '/sanctum/csrf-cookie',

  // Insurance bulk-discovery batches poll from a global Pinia store (see
  // stores/insurance/batch.ts) that must keep running while the user
  // navigates elsewhere. Cancelling a status request mid-navigation makes
  // it hang forever — the interceptor below never settles cancelled
  // requests — so pollInFlight never resets and polling stalls for good.
  '/insurance/bulk-discovery',

  // VH-3608: the Medicare / second-plan check poll (composables/useSecondPlanCheck.ts). The plan
  // workspace stays mounted across patient navigations, and a cancelled request would stall it.
  '/insurance/second-plan-checks',
] as const

function isRouteChangeCancelExempt(url: string | undefined): boolean {
  if (!url)
    return false

  return ROUTE_CHANGE_CANCEL_EXEMPT_URL_PREFIXES.some(prefix => url.startsWith(prefix))
}

const URL_PARAM_KEYS = [
  ['case_uuid', 'case_id'],
  ['opportunity_uuid', 'opportunity_uuid'],
  ['patient_uuid', 'patient_uuid'],
  ['provider_uuid', 'provider_uuid'],
  ['program_uuid', 'program_uuid'],
  ['icdcode_id', 'icdcode_id'],
] as const

function applyRouteParamsToUrl(url: string | undefined, params: Record<string, unknown>): string | undefined {
  if (!url)
    return url
  let out = url
  for (const [placeholder, paramKey] of URL_PARAM_KEYS) {
    const value = params[paramKey]
    if (out.includes(placeholder) && value != null)
      out = out.replace(placeholder, String(value))
  }

  return out
}

if (router?.beforeEach) {
  router.beforeEach((to, from, next) => {
    if (to.path !== from.path) {
      for (const source of pendingRequests) source.cancel('Canceled due to route change')

      pendingRequests.length = 0
    }
    next()
  })
}

axiosIns.interceptors.request.use(
  config => {
    localStorage.setItem('lastRequestTimestamp', new Date().getTime().toString())

    const params = router.currentRoute.value.params

    config.url = applyRouteParamsToUrl(config.url, params) ?? config.url

    config.headers.Accept = 'application/json'

    if (params.provider_uuid)
      config.headers['X-Provider-UUID'] = String(params.provider_uuid)

    const source = axios.CancelToken.source()

    config.cancelToken = source.token
    if (!isRouteChangeCancelExempt(config.url) && !config.skipRouteChangeCancel)
      pendingRequests.push(source)

    return config
  },
  error => {
    return Promise.reject(error)
  },
)

function reportApiErrorToRollbar(error: any, status: number | undefined) {
  const config = error.config
  const fullUrl = config?.baseURL ? `${config.baseURL.replace(/\/$/, '')}/${config?.url?.replace(/^\//, '')}` : config?.url

  const message = !error.response
    ? `API request failed: ${(error.code === 'ECONNABORTED') ? 'timeout' : (error.code || 'network error')}: ${config?.url ?? 'unknown'}`
    : `API error ${status}: ${config?.url}`

  // The medicaid PATCH body's `answers`/`people_answers` are keyed by catalog
  // path (e.g. "person.dob", "coverage_need.pregnant") — Rollbar's scrubFields
  // only redacts an exact key match (^dob$), so a dotted key like "person.dob"
  // is invisible to it and the raw patient answers would ship unredacted.
  // Drop the body/query for this route family entirely rather than trust
  // scrubbing to catch catalog-shaped keys it was never told about.
  const includeBody = !isMedicaidApplyRequest(config?.url)

  // Never send the body or query values themselves, only their shape (type + key names). By now
  // axios has serialized a JSON body to a string, which `scrubFields` can't see inside, so the raw
  // `config.data` shipped emails, passwords and reset tokens verbatim (VH-3151). Query params are
  // an object, but free-text values (a patient-name search) sit under keys no scrub list covers.
  rollbar.error(message, {
    request: {
      url: fullUrl,
      method: config?.method,
      params: includeBody ? summarizeRequestBody(config?.params) : undefined,
      data: includeBody ? summarizeRequestBody(config?.data) : undefined,
    },
    response: error.response
      ? { status: error.response.status, statusText: error.response.statusText, data: error.response.data }
      : undefined,
  })
}

async function handleAuthResponseError(
  auth: ReturnType<typeof useAuthStore>,
  status: number | undefined,
  error: any,
): Promise<unknown | null> {
  if (status === 401) {
    // Expected 401s (magic-link login, medicaid identity gate) must reject so
    // the caller can render the right page state. Swallowing them into a
    // never-settling promise left those pages spinning forever instead.
    if (isExpectedUnauthenticated401(error.config?.url))
      return null

    // Read localStorage directly so logout in another tab is reflected here.
    const isLoggedIn = !!localStorage.getItem('isAuthenticated')

    // Public pages (onboarding portal, SSDI patient form...) are reachable with no
    // session at all, so a 401 from some background call is not a reason to throw the
    // visitor off the page. Logging out here navigates to /login and loses their place
    // — for a portal visitor mid-upload, that's the whole task gone. Swallow it and
    // leave them on the page instead. (VH-2377)
    if (isLoggedIn && !isPublicRoute(router.currentRoute.value))
      auth.logout()

    // Never-settling promise so the rejection never reaches callers
    // (stores/components) and triggers error toasts.
    return new Promise(() => { /* never settles */ })
  }
  if (status === 429) {
    // Polling loops opt in via `isPollRequest` so they can see the 429 and back off
    // themselves; skip the generic toast (they surface their own status) and let the
    // rejection propagate instead of swallowing it into a never-settling promise.
    if (error.config?.isPollRequest || isMedicaidApplyRequest(error.config?.url))
      return null

    toast.error('Too many requests. Please wait a moment before trying again.')

    // Never-settling promise so the rejection never reaches callers as unhandled.
    return new Promise(() => { /* never settles */ })
  }
  if (status === 419) {
    // Guard against retry storms: only attempt the CSRF refresh + replay once.
    if (error.config?._csrfRetried)
      return null

    // Use the absolute API URL so the request reaches the Laravel backend in
    // all environments. { baseURL: '' } only works locally via the Vite proxy
    // and returns the SPA's index.html (text/html) in production, setting no cookie.
    await axiosIns.get(`${import.meta.env.VITE_SERVER_URL}/sanctum/csrf-cookie`)

    return axiosIns({ ...error.config, _csrfRetried: true })
  }

  return null
}

axiosIns.interceptors.response.use(
  response => {
    const token = response.config.cancelToken
    if (token) {
      const index = pendingRequests.findIndex(s => s.token === token)
      if (index !== -1)
        pendingRequests.splice(index, 1)
    }

    // Unwraps the common Laravel-resource-collection envelope ({data: [...]})
    // so callers can read response.data directly. This is a heuristic, not a
    // guaranteed contract: any endpoint that returns OTHER sibling keys
    // alongside `data` (e.g. {patient: {...}, data: [...]}) with no `meta`
    // key gets those sibling keys silently discarded here, which crashed a
    // real page before `skipDataUnwrap` existed (see the insurance-summary
    // endpoint's store, src/stores/insurance/patientSummary.ts). If you add a
    // new endpoint shaped like that, opt it out with `{skipDataUnwrap: true}`
    // on the request config rather than assuming this unwrap is harmless.
    if (response.data && response.data.data && !response.data.meta && !response.config.skipDataUnwrap)
      response.data = response.data.data

    return Promise.resolve(response)
  },
  async error => {
    const auth = useAuthStore()
    const status = _.get(error, 'response.status')
    const env = import.meta.env.VITE_NODE_ENV
    const token = error?.config?.cancelToken

    if (token) {
      const index = pendingRequests.findIndex(s => s.token === token)
      if (index !== -1)
        pendingRequests.splice(index, 1)
    }

    if (isCancel(error))
      return new Promise(() => { /* never settles — cancelled request */ })
    if (!error.response && env === 'staging')
      return Promise.reject(error)

    // Polling loops report rate limits themselves, at most once per burst
    // (see `usePollingWithBackoff`) — skip the default per-request report here.
    const skipRateLimitReport = status === 429 && (!!error.config?.isPollRequest || isMedicaidApplyRequest(error.config?.url))

    // Expected 401s (magic-link login, medicaid identity gate) are handled by
    // the caller — not unhandled errors worth paging on.
    const isExpected401 = isExpectedUnauthenticated401(error.config?.url) && status === 401

    // A first 419 is handled below by refreshing the CSRF cookie and replaying the request once.
    // Only the replay's failure is worth reporting (it comes back through this interceptor with
    // `_csrfRetried` set); reporting the first one paged on 419s the retry had already fixed.
    const isRetryable419 = status === 419 && !error.config?._csrfRetried

    const reportToRollbar = env === 'production' || env === 'staging'

    // 422 is Laravel's standard validation-failure status — by convention it always
    // means the client sent input the server rejected as invalid, never a server bug,
    // so (like 404) it's never worth reporting to Rollbar.
    if (reportToRollbar && status !== 404 && status !== 422 && !skipRateLimitReport && !isExpected401 && !isRetryable419)
      reportApiErrorToRollbar(error, status)

    const authResult = await handleAuthResponseError(auth, status, error)
    if (authResult !== null)
      return authResult

    return Promise.reject(error)
  },
)

export default axiosIns
